/**
 * ContentHeader: /video-renewal 전용 상단 제목 바.
 *
 * v1 원본: RenewalComponents/SVContent/Header/HeaderPresenter.js
 *         + ConferenceInfo (회의 정보 팝오버)
 * v1 CSS: .rtc_new3 .viewerHeader { position: absolute; top: 0; height: 54px; ... }
 *
 * ⚠ .content.content--viewer 내부에 렌더링되어야 CSS가 적용됨.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import { toastActions } from '@/store/slices/toastSlice';
import Timer from '@/components/Footer/timers/Timer';
import ScreenModeButton from '@/components/SVContent/ScreenModeButton';
import { toggleFullScreen, getShareUrl } from '@/utils/urlUtils';
import { formatDateTime } from '@/utils/formatUtils';
import { isMasterUser } from '@/store/slices/masterSlice';

interface ContentHeaderProps {
  roomName: string;
  onStopRecording?: () => void;
}

// ─── ConferenceInfo 팝오버 ─────────────────────────────────────────────
interface ConferenceInfoProps {
  title: string;
  conferenceId: string;
  shareUrl: string;
  password: string;
  hasPassword: boolean;
  onCopy: () => void;
}

/** 회의 ID 포맷: 123456789 → 123-456-789 */
const formatConferenceId = (id: string): string =>
  id ? id.replace(/(\d{3})(?=\d)/g, '$1-') : '';

const ConferenceInfo: React.FC<ConferenceInfoProps> = ({ title, conferenceId, shareUrl, password, hasPassword, onCopy }) => (
  <div className="serviceTitle__layer open">
    <strong className="serviceTitle__layerTitle ellipsis" title={title}>
      {title}
    </strong>
    <dl className="serviceTitle__layerList">
      {conferenceId && (
        <div>
          <dt>회의 ID</dt>
          <dd>{formatConferenceId(conferenceId)}</dd>
        </div>
      )}
      {hasPassword && password && (
        <div>
          <dt>암호</dt>
          <dd>{password}</dd>
        </div>
      )}
      <div>
        <dt>회의 링크</dt>
        <dd className="ellipsis" style={{ maxWidth: 180 }} title={shareUrl}>{shareUrl}</dd>
      </div>
    </dl>
    <div className="serviceTitle__footer">
      <button
        type="button"
        className="common_round_btn blue3 serviceTitle__copyBtn"
        onClick={onCopy}
      >
        회의 정보 복사
      </button>
    </div>
  </div>
);

// ─── ContentHeader 메인 ────────────────────────────────────────────────
const ContentHeader: React.FC<ContentHeaderProps> = ({ roomName, onStopRecording }) => {
  const dispatch = useAppDispatch();
  const recording = useAppSelector((s) => s.recording);
  const local = useAppSelector((s) => s.local);
  const mainUser = useAppSelector((s) => s.mainUser);
  const ui = useAppSelector((s) => s.ui);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  const isLocalMaster = isMasterUser(masterUsers, local.wehagoId);

  const [isOpenConferenceInfo, setIsOpenConferenceInfo] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);

  const title = roomName || '회의실';
  const shareUrl = getShareUrl();
  const hasPassword = local.securityType === 'PASSWORD';

  // 팝오버 외부 클릭 닫기
  useEffect(() => {
    if (!isOpenConferenceInfo) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (titleRef.current && !titleRef.current.contains(e.target as Node)) {
        setIsOpenConferenceInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpenConferenceInfo]);

  const handleToggleConferenceInfo = useCallback(() => {
    setIsOpenConferenceInfo((prev) => !prev);
  }, []);

  const handleCopyConferenceInfo = useCallback(async () => {
    let copyMsg = `회의명 : ${title}`;
    if (local.startDatetime) {
      copyMsg += `\n시간 : ${formatDateTime(local.startDatetime)}`;
    }
    copyMsg += `\n링크 : ${shareUrl}`;
    if (local.conferenceId) {
      copyMsg += `\n회의 ID : ${formatConferenceId(local.conferenceId)}`;
    }
    if (hasPassword && local.joinCode) {
      copyMsg += `\n암호 : ${local.joinCode}`;
    }
    try {
      await navigator.clipboard.writeText(copyMsg);
    } catch {
      // clipboard API 미지원 폴백
      const ta = document.createElement('textarea');
      ta.value = copyMsg;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    dispatch(toastActions.showSnackbar({ type: 'success', message: '회의 정보가 복사되었습니다.' }));
  }, [title, shareUrl, hasPassword, local.startDatetime, local.conferenceId, local.joinCode, dispatch]);

  // ─── 고정된 화면으로 돌아가기 ──────────────────────────────────────────
  const hasPins = mainUser.masterPinnedUserIds.length > 0 || mainUser.pinnedUserIds.length > 0;
  const showReturnToPinBtn =
    hasPins && (ui.screenMode !== 'PRESENTER' || ui.presenterOverrideUserId !== null);

  const handleReturnToPin = useCallback(() => {
    dispatch(uiActions.setPresenterOverrideUserId(null));
    dispatch(uiActions.setIsShareClick(false));
    if (ui.screenMode !== 'PRESENTER') {
      dispatch(uiActions.setScreenMode('PRESENTER'));
    }
  }, [dispatch, ui.screenMode]);

  // ── 녹화 중 UI 상태 ────────────────────────────────────────────────────────
  const [isRecordingLayerOpen, setIsRecordingLayerOpen] = useState(false);
  const recLayerRef = useRef<HTMLDivElement>(null);

  const toggleRecordingLayer = useCallback(() => {
    setIsRecordingLayerOpen((prev) => !prev);
  }, []);

  // 녹화 정보 팝업 외부 클릭 닫기
  useEffect(() => {
    if (!isRecordingLayerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (recLayerRef.current && !recLayerRef.current.contains(e.target as Node)) {
        setIsRecordingLayerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isRecordingLayerOpen]);

  // 녹화 종료 시 팝업 자동 닫기
  useEffect(() => {
    if (!recording.isRecording) {
      setIsRecordingLayerOpen(false);
    }
  }, [recording.isRecording]);

  // 경과 시간 계산 (1초 간격 갱신)
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!recording.isRecording || !recording.startTime) {
      setElapsed(0);
      return;
    }
    // 초기값 즉시 반영
    setElapsed(Date.now() - recording.startTime);
    const timer = setInterval(() => {
      setElapsed(Date.now() - recording.startTime!);
    }, 1000);
    return () => clearInterval(timer);
  }, [recording.isRecording, recording.startTime]);

  // 타이머 뱃지용 "02:22" 형식
  const formattedDuration = useMemo(() => {
    const totalSec = Math.floor(elapsed / 1000);
    const mm = String(Math.floor(totalSec / 60)).padStart(2, '0');
    const ss = String(totalSec % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [elapsed]);

  // 팝업용 "2분 22초" 형식
  const formattedDurationLong = useMemo(() => {
    const totalSec = Math.floor(elapsed / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}분 ${s}초`;
  }, [elapsed]);

  // 녹화 시작 시각 "16:56" 형식
  const formattedStartTime = useMemo(() => {
    if (!recording.startTime) return '--:--';
    const d = new Date(recording.startTime);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }, [recording.startTime]);

  return (
    <div className="viewerHeader">
      {/* 좌측: 제목(클릭 시 회의 정보 팝오버) + 타이머 */}
      <div className="serviceTitle" ref={titleRef}>
        <button
          type="button"
          className="serviceTitle__btn"
          title={title}
          onClick={handleToggleConferenceInfo}
        >
          <h2 className="ellipsis">{title}</h2>
        </button>
        {isOpenConferenceInfo && (
          <ConferenceInfo
            title={title}
            conferenceId={local.conferenceId}
            shareUrl={shareUrl}
            password={local.joinCode}
            hasPassword={hasPassword}
            onCopy={handleCopyConferenceInfo}
          />
        )}
        <span>
          <Timer />
        </span>
      </div>

      {/* 우측: 보기 + 녹화 + 전체화면 */}
      <div className="viewerHeader__btnBox">
        {showReturnToPinBtn && (
          <div className="viewerHeader__btnItem">
            <button type="button" className="viewerHeader__btn" onClick={handleReturnToPin}>
              고정된 화면으로 돌아가기
              <span className="lunacon lunacon--refresh-outlined"></span>
            </button>
          </div>
        )}
        {recording.isRecording && (
          <>
            <div className="viewerHeader__btnItem" ref={recLayerRef}>
              {/* 경과 시간 뱃지 (빨간 라운드) */}
              <button type="button" className="recTextBtn" onClick={toggleRecordingLayer}>
                <span className="recText">{formattedDuration}</span>
              </button>
              {/* 깜빡이는 녹화 아이콘 */}
              <button
                type="button"
                className="viewerHeader__btn viewerHeader__btn--nobg"
                onClick={toggleRecordingLayer}
              >
                <span className="viewerHeader__btnIcon ico_recPlay motionPlay">녹화중지</span>
              </button>
              {/* 녹화 정보 팝업 레이어 */}
              {isRecordingLayerOpen && (
                <div className="recPlay__layer open">
                  <strong className="recPlay__layerTitle">회의 녹화중</strong>
                  <dl className="recPlay__layerList">
                    <div>
                      <dt>녹화시작</dt>
                      <dd>{formattedStartTime}</dd>
                    </div>
                    <div>
                      <dt>진행시간</dt>
                      <dd>{formattedDurationLong}</dd>
                    </div>
                  </dl>
                  {isLocalMaster && (
                    <div className="serviceTitle__footer">
                      <button
                        type="button"
                        className="common_round_btn blue3"
                        onClick={onStopRecording}
                      >
                        녹화중지
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <span className="viewerHeader__btnBar"></span>
          </>
        )}
        <ScreenModeButton />
        <div className="viewerHeader__btnItem">
          <button type="button" className="viewerHeader__btn" onClick={toggleFullScreen}>
            <span className="viewerHeader__btnIcon iconAllShow">전체화면</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentHeader;
